import React, { useEffect } from 'react';

import AppLayout from '@amzn/meridian/app-layout';

import { createBrowserHistory } from 'history';
import ReactDOM from 'react-dom/client';
import { Route, Router, Switch, useLocation } from 'react-router-dom';
import { DeepLinkViewer } from 'src/components/Agreements/DocumentDetail/DeepLinkViewer';
import { Analytics } from 'src/components/Analytics';
import { CompareContracts } from 'src/components/CompareContracts';
import { MonthOverMonthPayments } from 'src/components/MonthOverMonthPayments';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { Reporting } from 'src/components/Reporting/Reporting';
import { ReviewPaperPlan } from 'src/components/ReviewPaperPlan/ReviewPaperPlan';
import { ServiceLaunches } from 'src/components/ServiceLaunches';
import { SideBar2 } from 'src/components/SideBar/SideBar2';
import { UserSettings } from 'src/components/UserSettings';

import { AI } from './components/AI';
import { Agreements } from './components/Agreements';
import { Authenticator } from './components/Authenticator';
import { BasePlanLaunchDates } from './components/BasePlanLaunchDates';
import { BundlePricing } from './components/BundlePricing';
import { CompareDeals } from './components/CompareDeals';
import { CurrencyAndContentAcquisitionEntity } from './components/CurrencyAndContentAcquisitionEntity';
import { Deals } from './components/Deals';
import { Home } from './components/Home';
import { PaperPlanMappings } from './components/PaperPlanMappings';
import { PaperPlans } from './components/PaperPlans';
import { PlanWeights } from './components/PlanWeights';
import { RateCards } from './components/RateCards';
import { PageNotFound } from './components/Redirects';
import { ReleaseNotes } from './components/ReleaseNotes';
import { RightSideBar } from './components/RightSideBar';
import { RoyaltyManager } from './components/RoyaltyManager';
import { ExecutionRequestDetails } from './components/RoyaltyManager/ExecutionRequestDetails';
import { ToasterWrapper } from './components/Toaster';
import { TopAlert } from './components/TopAlert';
import { TopBar } from './components/TopBar';
import { Vendors } from './components/Vendors';
import { ROUTES, URL_PARAMS } from './constants/routes';
import { HEIGHT_ADJUSTMENT, TOPBAR_HEIGHT } from './constants/style';
import { AppProvider } from './contexts/appContext';
import './index.scss';
import { getWindowSize } from './util/PlanMappings';
import initRumClient from './util/cloudwatchRum/rumHelper';
import { isOnGammaProd } from './util/config';

const history = createBrowserHistory();

const SessionStorageCleaner: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        if (location.pathname !== '/') {
            sessionStorage.removeItem('intendedPath');
        }
    }, [location.pathname]);

    return null;
};

const AppContent: React.FC = () => {
    const routes = (
        <Switch>
            <ProtectedRoute exact path="/">
                <Home />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.DEALS.INDEX}>
                <Deals />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.REPORTING.INDEX}>
                <Reporting />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.VENDORS.INDEX}>
                <Vendors />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.RATE_CARDS.INDEX}>
                <RateCards />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.PAPER_PLANS.INDEX}>
                <PaperPlans />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.PLAN_WEIGHTS.INDEX}>
                <PlanWeights />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.PAPER_PLAN_MAPPINGS.INDEX}>
                <PaperPlanMappings />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX}>
                <BasePlanLaunchDates />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.SERVICE_LAUNCHES.INDEX}>
                <ServiceLaunches />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.CURRENCY_CONTENT_ACQUISITION_ENTITY.INDEX}>
                <CurrencyAndContentAcquisitionEntity />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.BUNDLE_PRICING.INDEX}>
                <BundlePricing />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.RELEASE_NOTES.INDEX}>
                <ReleaseNotes />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.COMPARE.CONTRACTS}>
                <CompareContracts />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.COMPARE.DEALS}>
                <CompareDeals />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.ANALYTICS.INDEX}>
                <Analytics />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.ANALYTICS.MONTH_OVER_MONTH_PAYMENTS}>
                <MonthOverMonthPayments />
            </ProtectedRoute>
            <ProtectedRoute exact path={ROUTES.AI.INDEX}>
                <AI />
            </ProtectedRoute>
            <ProtectedRoute exact path={`${ROUTES.AI.INDEX}/:conversationId`}>
                <AI />
            </ProtectedRoute>
            <ProtectedRoute path={`${ROUTES.AI.INDEX}/:conversationId/:timestamp`}>
                <AI />
            </ProtectedRoute>
            <ProtectedRoute path={`${ROUTES.SETTINGS.INDEX}`}>
                <UserSettings />
            </ProtectedRoute>
            <ProtectedRoute path={`${ROUTES.ROYALTY_MANAGER.INDEX}${URL_PARAMS.EXECUTION_REQUEST_ID}`}>
                <ExecutionRequestDetails />
            </ProtectedRoute>
            <ProtectedRoute path={ROUTES.ROYALTY_MANAGER.INDEX}>
                <RoyaltyManager />
            </ProtectedRoute>
            <ProtectedRoute exact path={ROUTES.AGREEMENTS.INDEX}>
                <Agreements />
            </ProtectedRoute>
            <ProtectedRoute path={`${ROUTES.AGREEMENTS.INDEX}/:tenantId/:documentId`}>
                <DeepLinkViewer />
            </ProtectedRoute>
            {!isOnGammaProd() && (
                <ProtectedRoute path={ROUTES.REVIEW_PAPER_PLANS.INDEX}>
                    <ReviewPaperPlan />
                </ProtectedRoute>
            )}
            <Route component={PageNotFound} />
        </Switch>
    );

    return (
        <AppLayout headerComponent={TopBar} sidebarComponent={SideBar2} sheetComponent={RightSideBar}>
            <TopBar />
            <ToasterWrapper />
            <TopAlert />
            <SideBar2 />
            <RightSideBar />
            <div
                className="main-content"
                style={{
                    height: getWindowSize().height - (TOPBAR_HEIGHT + HEIGHT_ADJUSTMENT)
                }}
            >
                {routes}
            </div>
        </AppLayout>
    );
};

const App: React.FC = () => {
    useEffect(() => {
        initRumClient();
    }, []);

    return (
        <Authenticator>
            <AppProvider>
                <Router history={history}>
                    <SessionStorageCleaner />
                    <AppContent />
                </Router>
            </AppProvider>
        </Authenticator>
    );
};

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);
